<script setup lang="ts">
import { AppPermissionsEnum } from '@/api/generator';
import Dialog from '@/components/Dialog/Dialog.vue';
import { error, warningSign } from '@/components/icons/icons.ts';
import IdentifierLabel from '@/components/IdentifierLabel/IdentifierLabel.vue';
import { routenames } from '@/router/routenames.ts';
import { useLicenseStore } from '@/stores/licenseStore.ts';
import { usePermissionsStore } from '@/stores/permissionsStore.ts';
import { storeToRefs } from 'pinia';
import { useRouter } from 'vue-router';
import { computed } from 'vue';

type UnicheckDeviceOfflineInfoDialogProps = {
  modelValue: boolean;
};

defineProps<UnicheckDeviceOfflineInfoDialogProps>();
const emit = defineEmits<{
  'update:modelValue': [value: boolean];
}>();

const router = useRouter();
const { hasPermissions } = usePermissionsStore();

const licenseStore = useLicenseStore();
const {
  licenseIsAboutToExpire,
  daysToLicenseExpiration,
  deviceLicensesAboutToExpire,
  earliestExpirationDays,
  licenseHasExpired,
  expiredDeviceLicenses,
} = storeToRefs(licenseStore);

const licenseExpirationAlertTitle = computed(() =>
  licenseHasExpired.value ? 'LicenseHasExpired' : 'YourLicenseIsAboutToExpireSoon',
);

const licenseHasExpiredMessage = computed(() =>
  licenseHasExpired.value ? 'LicenseHasExpiredMsg' : 'YourLicenseWillExpireIn_count_DaysMsg',
);

function goToLicensePage() {
  router.push({ name: routenames.licenses });
  emit('update:modelValue', false);
}
</script>

<template>
  <Dialog
    :modelValue="modelValue"
    aria-label="RequestAssignmentDialog"
    minWidth="480"
    maxWidth="800"
    @update:modelValue="$emit('update:modelValue', $event)"
  >
    <template #title>
      <span v-if="expiredDeviceLicenses.size > 0" class="mb-6">
        {{ $t('SomeLicenseFeaturesHaveExpired') }}
      </span>
      <span v-else>
        {{
          $t('SomeLicenseFeaturesWillExpireIn_count_Days', {
            count: earliestExpirationDays,
          })
        }}
      </span>
    </template>
    <template #text>
      <v-alert
        v-if="licenseIsAboutToExpire"
        :type="licenseHasExpired ? 'error' : 'warning'"
        :title="$t(licenseExpirationAlertTitle)"
        :icon="licenseHasExpired ? error : warningSign"
        class="mb-6 text-left"
      >
        <p class="mb-2">
          {{
            $t(licenseHasExpiredMessage, {
              count: daysToLicenseExpiration,
            })
          }}
        </p>
        <p class="mb-2">{{ $t('LicenseHasExpiredRemedy') }}</p>
      </v-alert>

      <div v-if="expiredDeviceLicenses.size > 0" class="mb-6">
        <h3>
          {{
            $t('TheFollwing_count_DevicesLicensesHaveExpired', {
              count: expiredDeviceLicenses.size,
            })
          }}
        </h3>

        <ul class="ml-5">
          <li
            v-for="deviceLicenseInfo in expiredDeviceLicenses.values()"
            :key="deviceLicenseInfo.id"
          >
            <div class="d-flex ga-1">
              <IdentifierLabel
                v-if="deviceLicenseInfo.name && deviceLicenseInfo.iuid"
                :identifier="deviceLicenseInfo.iuid"
                :label="deviceLicenseInfo.name"
              />
              <IdentifierLabel
                v-else
                :identifier="deviceLicenseInfo.subModel"
                :label="deviceLicenseInfo.model"
              />
              <div>
                {{
                  $t('WithSerialNumber_serialNumber_HasExpired', {
                    serialNumber: deviceLicenseInfo.serialNumber,
                  })
                }}
              </div>
            </div>
          </li>
        </ul>
      </div>
      <div v-if="deviceLicensesAboutToExpire.size > 0">
        <h3>
          {{
            $t('TheFollwing_count_DevicesLicensesWillExpireSoon', {
              count: deviceLicensesAboutToExpire.size,
            })
          }}
        </h3>

        <ul class="ml-5">
          <li
            v-for="deviceLicenseInfo in deviceLicensesAboutToExpire.values()"
            :key="deviceLicenseInfo.id"
          >
            <div class="d-flex ga-1">
              <IdentifierLabel
                v-if="deviceLicenseInfo.name && deviceLicenseInfo.iuid"
                :identifier="deviceLicenseInfo.iuid"
                :label="deviceLicenseInfo.name"
              />
              <IdentifierLabel
                v-else
                :identifier="deviceLicenseInfo.subModel"
                :label="deviceLicenseInfo.model"
              />
              <div>
                {{
                  $t('WithSerialNumber_serialNumber_ExpiresIn_count_Days', {
                    serialNumber: deviceLicenseInfo.serialNumber,
                    count: deviceLicenseInfo.daysToExpiration,
                  })
                }}
              </div>
            </div>
          </li>
        </ul>
      </div>
    </template>
    <template #actions>
      <VSpacer />
      <PrimaryVBtn
        :text="$t('Ok')"
        class="align-self-end"
        minWidth="8rem"
        @click="() => $emit('update:modelValue', false)"
      />
      <PrimaryVBtn
        v-if="
          router.currentRoute.value.name !== routenames.licenses &&
          hasPermissions([AppPermissionsEnum.GlobalLicensesViewDetail])
        "
        :text="$t('GoToLicensePage')"
        class="align-self-end"
        minWidth="8rem"
        @click="goToLicensePage"
      />
    </template>
  </Dialog>
</template>
